import { useState } from "react";
import { Product, OrderedProduct } from "src/shared/services/interfacev2";
import { ProductNote } from "src/shared/types";

/*** Safely extract Mongo ObjectId as string*/
const getProductId = (product: Product): string => {
  if (typeof product._id === "string") return product._id;
  return product._id?.$oid ?? "";
};


/*** Convert Product → OrderedProduct*/
const mapProductToOrderedProduct = (product: Product): OrderedProduct => {
  const unitPrice = Number(product.amount) || 0;
  const quantity = 1;

  return {
    productId: getProductId(product),
    name: product.productName || "",
    arabicName: product.arabicName || "",
    quantity: quantity,
    unitPrice: unitPrice,
    lineTotal: quantity * unitPrice, // ✅ Fixed: use quantity * unitPrice
    type: product.type || "",
  };
};

export const useOrderManagement = () => {
  /** ✅ MUST be OrderedProduct[] */
  const [selectedProducts, setSelectedProducts] = useState<OrderedProduct[]>([]);
  const [discount, setDiscount] = useState<number>(0);
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const [parcelFee, setParcelFee] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);

  const calculateSubtotal = (): number => {
    return selectedProducts.reduce((sum, p) => {
      // Ensure lineTotal is a number
      const lineTotal = p.lineTotal ?? ((p.unitPrice * p.quantity) || 0);
      return sum + lineTotal;
    }, 0);
  };

  const calculateTotal = (subtotal: number, isDelivery: boolean = true): number => {
    return (
      subtotal
      - discount
      + (isDelivery ? deliveryFee : 0)
      + parcelFee
    );
  };

  /** Add product from menu (Product) */
  const addProduct = (product: Product) => {
    const id = getProductId(product);
    setSelectedProducts((prev) => {
      const existing = prev.find((p) => p.productId === id);
      if (existing) {
        return prev.map((p) => {
          if (p.productId === id) {
            const newQuantity = p.quantity + 1;
            return {
              ...p,
              quantity: newQuantity,
              lineTotal: newQuantity * p.unitPrice
            };
          }
          return p;
        });
      }
      return [...prev, mapProductToOrderedProduct(product)];
    });
  };

  /** Decrement product from menu (Product) */
  const ProductDecrement = (product: Product) => {
    const productId = getProductId(product);
    
    // Find the cart item
    const cartItem = selectedProducts.find((p) => p.productId === productId);
    
    if (cartItem) {
      // If quantity is 1, remove the item
      if (cartItem.quantity <= 1) {
        deleteProduct(productId);
      } else {
        // Otherwise decrement
        decrementCart(cartItem);
      }
    }
  };

  // Increment in cart
  const incrementCart = (item: OrderedProduct) => {
    setSelectedProducts((prev) =>
      prev.map((p) => {
        if (p.productId === item.productId) {
          const newQuantity = p.quantity + 1;
          return {
            ...p,
            quantity: newQuantity,
            lineTotal: newQuantity * p.unitPrice
          };
        }
        return p;
      })
    );
  };

  // Decrement in cart
  const decrementCart = (item: OrderedProduct) => {
    setSelectedProducts((prev) =>
      prev
        .map((p) => {
          if (p.productId === item.productId) {
            const newQuantity = p.quantity - 1;
            // If quantity becomes 0 or less, we'll filter it out
            if (newQuantity <= 0) {
              return null;
            }
            return {
              ...p,
              quantity: newQuantity,
              lineTotal: newQuantity * p.unitPrice
            };
          }
          return p;
        })
        .filter((p): p is OrderedProduct => p !== null) // Remove null items
    );
  };

  /** Delete product from cart */
  const deleteProduct = (productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.productId !== productId));
  };

  /** Update notes for a product in cart */
  const handleNoteChange = (productId: string, note: ProductNote[]) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.productId === productId ? { ...p, notes: note } : p
      )
    );
  };

  return {
    selectedProducts,
    setSelectedProducts,
    discount,
    setDiscount,
    deliveryFee,
    setDeliveryFee,
    isEditing,
    setIsEditing,
    editingNote,
    setEditingNote,
    calculateSubtotal,
    calculateTotal,
    addProduct,
    ProductDecrement,
    incrementCart,
    decrementCart,
    deleteProduct,
    handleNoteChange,
    parcelFee,
    setParcelFee,
  };
};